package me.tbsten.compose.preview.lab

import androidx.compose.runtime.Composable
import kotlin.js.ExperimentalJsExport
import me.tbsten.compose.preview.lab.util.JsOnlyExport
import me.tbsten.compose.preview.lab.util.JsOnlyExportIgnore

/**
 * Have [@Preview][me.tbsten.compose.preview.lab.gallery.previewlist.PreviewTreeNode.Preview] information collected by gradle plugin. It can be used to display the Preview catalog by passing it to PreviewLabGallery.
 *
 * ```kt
 * // generated by gradle plugin
 * package myApp
 * object PreviewList : List<CollectedPreview> by listOf(...)
 *
 * // user code
 * fun MyAppEntryPoint() {
 *   ComposePreviewLabGallery(
 *     previewList = myApp.PreviewList,
 *   )
 * }
 * ```
 *
 * In addition to collecting from the gradle plugin, you can also use the CollectedPreview function to represent a pseudo-Preview.
 *
 * ```kt
 * fun MyAppEntryPoint() {
 *   ComposePreviewLabGallery(
 *     previews = myAppPreviews + listOf(
 *       CollectedPreview(
 *         displayName = "MyButtonPreview",
 *         filePath = "myApp/src/main/kotlin/myApp/MyButtonPreview.kt",
 *         startLineNumber = 10,
 *         code = null,
 *       ) {
 *         MyButton(text = fieldValue { StringField("Click!") })
 *       },
 *     ),
 *   )
 * }
 * ```
 *
 * @property displayName The value set to displayName of the ComposePreviewLabOption annotation.
 * @property filePath The file where Preview exists.
 * @property startLineNumber The starting line number of Preview (1-based).
 * @property endLineNumber The ending line number of Preview (1-based, inclusive).
 * @property code The source code of Preview.
 * @property content The content of Preview. You can display the content of Preview by calling invoke().
 *
 * @see CollectedPreview
 */
interface PreviewLabPreview {
    val id: String
    val displayName: String
    val filePath: String?
    val startLineNumber: Int?
    val endLineNumber: Int?
    val code: String?
    val kdoc: String?
    val content: @Composable () -> Unit
}

@OptIn(InternalComposePreviewLabApi::class, ExperimentalJsExport::class)
@JsOnlyExport
data class CollectedPreview @JsOnlyExportIgnore constructor(
    override val id: String,
    override val displayName: String = id,
    override val filePath: String? = null,
    override val startLineNumber: Int? = null,
    override val endLineNumber: Int? = null,
    override val code: String? = null,
    override val kdoc: String? = null,
    override val content: @Composable () -> Unit,
) : PreviewLabPreview

/**
 * Create a [CollectedPreview] instance.
 *
 * @param displayName The value set to displayName of the ComposePreviewLabOption annotation.
 * @param filePath The file where Preview exists.
 * @param startLineNumber The starting line number of Preview (1-based).
 * @param endLineNumber The ending line number of Preview (1-based, inclusive).
 * @param code The source code of Preview.
 * @param content The content of Preview. You can display the content of Preview by calling invoke().
 */
fun PreviewLabPreview(
    id: String,
    displayName: String = id,
    filePath: String? = null,
    startLineNumber: Int? = null,
    endLineNumber: Int? = null,
    code: String? = null,
    kdoc: String? = null,
    content: @Composable () -> Unit,
): PreviewLabPreview = object : PreviewLabPreview {
    override val id: String = id
    override val displayName: String = displayName
    override val filePath: String? = filePath
    override val startLineNumber: Int? = startLineNumber
    override val endLineNumber: Int? = endLineNumber
    override val code: String? = code
    override val kdoc: String? = kdoc
    override val content: @Composable (() -> Unit) = content
}
